// Single source of truth for the admin panel's online/offline indicator.
// Keep DEVICE_ONLINE_WINDOW_MS in sync with backend-ai's admin devices route,
// which uses the same window for its `status=online|offline` filter.
export const DEVICE_ONLINE_WINDOW_MS = 2 * 60 * 60 * 1000; // 2 hours

export function isDeviceOnline(
  lastConnectedAt: string | null | undefined
): boolean {
  if (!lastConnectedAt) return false;
  return Date.now() - new Date(lastConnectedAt).getTime() < DEVICE_ONLINE_WINDOW_MS;
}

/**
 * Freshness of a device's last contact, for the customer-facing status dots
 * (Tracks device menu, Devices list) and the Tracks map markers. Counted in
 * whole elapsed hours: "fresh" under 2 h, "recent" under 25 h, "stale" older,
 * "never" without a usable timestamp. A timestamp slightly in the future
 * (clock skew) counts as fresh. Separate from the admin panel's 2 h
 * isDeviceOnline above, which mirrors a backend filter.
 */
export type DeviceFreshness = "fresh" | "recent" | "stale" | "never";

export function deviceFreshness(
  lastSeen: Date | string | null | undefined,
  now: number = Date.now()
): DeviceFreshness {
  if (lastSeen == null) return "never";
  const t = new Date(lastSeen).getTime();
  if (Number.isNaN(t)) return "never";
  const hours = Math.trunc((now - t) / 3_600_000);
  if (hours <= 1) return "fresh";
  if (hours <= 24) return "recent";
  return "stale";
}

/** Tracks map marker fill per freshness; the map paints "never" grey. */
export const FRESHNESS_FILL: Record<DeviceFreshness, string> = {
  fresh: "rgb(30, 166, 114)",
  recent: "rgb(253, 224, 71)",
  stale: "rgb(209, 213, 219)",
  never: "rgb(209, 213, 219)",
};

/** Compact "last seen" label for list rows: "12 min ago", "3 d ago". */
export function formatLastOnline(
  lastConnectedAt: string | null | undefined
): string {
  if (!lastConnectedAt) return "never";
  const diff = Date.now() - new Date(lastConnectedAt).getTime();
  if (diff < 0) return "just now";
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} mo ago`;
  return `${Math.floor(days / 365)} y ago`;
}

/**
 * Deep link to the shared "devices" Grafana dashboard, scoped to one ESN.
 *
 * The dashboard lives on the Tailscale host, so the link only resolves for
 * admins on the tailnet — it is a staff shortcut, not a user-facing feature.
 * The uid (`5U0Kuq9Vz`) and the variable name (`var-device`) are what that
 * dashboard is actually registered under; a wrong uid silently lands on
 * Grafana's "dashboard not found" page.
 */
export const GRAFANA_DEVICES_DASHBOARD =
  "https://grafana.tail8237a.ts.net/d/5U0Kuq9Vz/devices";

export function grafanaDeviceUrl(esn: string): string {
  return `${GRAFANA_DEVICES_DASHBOARD}?orgId=1&var-device=${encodeURIComponent(esn)}`;
}

/** Full timestamp for the tooltip next to the relative label. */
export function formatLastOnlineExact(
  lastConnectedAt: string | null | undefined
): string {
  if (!lastConnectedAt) return "Never connected";
  return new Date(lastConnectedAt).toLocaleString();
}
